import heic2any from 'heic2any';

class HeicConversionService {
    constructor() {
        // Cache for converted images: Map<originalUrl, convertedBlobUrl>
        this.conversionCache = new Map();
        // Track conversion promises to avoid duplicate conversions
        this.conversionPromises = new Map();
    }

    /**
     * Check if a file is HEIC/HEIF format
     */
    isHeicFormat(filename) {
        if (!filename) return false;
        const extension = filename.split('.').pop().toLowerCase();
        return extension === 'heic' || extension === 'heif';
    }

    /**
     * Get converted JPEG URL for a HEIC file
     * Returns cached version if available, otherwise converts and caches
     */
    async getConvertedImage(originalUrl, filename, apiContext = null) {
        if (!this.isHeicFormat(filename)) {
            return originalUrl;
        }

        if (this.conversionCache.has(originalUrl)) {
            return this.conversionCache.get(originalUrl);
        }

        if (this.conversionPromises.has(originalUrl)) {
            return await this.conversionPromises.get(originalUrl);
        }

        const conversionPromise = this._convertHeicToJpeg(originalUrl, apiContext);
        this.conversionPromises.set(originalUrl, conversionPromise);

        try {
            const convertedUrl = await conversionPromise;
            this.conversionCache.set(originalUrl, convertedUrl);
            return convertedUrl;
        } catch (error) {
            console.error('HEIC conversion failed:', error);
            return originalUrl;
        } finally {
            this.conversionPromises.delete(originalUrl);
        }
    }

    /**
     * Internal method to perform HEIC to JPEG conversion
     */
    async _convertHeicToJpeg(originalUrl) {
        const response = await fetch(originalUrl, {mode: 'cors'});

        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }

        const file = await response.blob();
        const blob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9
        });

        return URL.createObjectURL(blob);
    }

    /**
     * Clear conversion cache (useful for memory management)
     */
    clearCache() {
        // Revoke all blob URLs to free memory
        for (const blobUrl of this.conversionCache.values()) {
            if (blobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrl);
            }
        }
        this.conversionCache.clear();
        this.conversionPromises.clear();
    }

    /**
     * Remove specific item from cache
     */
    removeFromCache(originalUrl) {
        const blobUrl = this.conversionCache.get(originalUrl);
        if (blobUrl && blobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrl);
        }
        this.conversionCache.delete(originalUrl);
    }
}

export default new HeicConversionService();